import { ChevronLeft, Loader2, LogOut } from 'lucide-react'
import type {
  CompleteOnboarding,
  CreateCheckoutSession,
  CreateProCrew,
  SendCrewInvite,
  LoadConfig,
  LoadMyEntitlements,
  LoadUserProfile,
  SendOtp,
  SetSession,
  UpdateMyProfile,
  UploadProfilePhoto,
  ValidatePromoCode,
  VerifyOtp,
  ClaimContact,
  SendContactClaimOtp,
} from '@/modules/auth/use-cases'
import type {
  LoadCurrentDocument,
  RecordLegalAcceptance,
} from '@/modules/legal/use-cases'
import type { EmitFunnelEvent } from '@/shared/domain'
import { Button } from '@/shared/ui/primitives'
import {
  AuthStep,
  CheckoutStep,
  CrewExplainerStep,
  CrewNameStep,
  FeaturePageStep,
  HunterProfileStep,
  OrgFormStep,
  RegisterLayout,
  RegisterProgressBar,
  RegisterStepCard,
  RegisterStepHeading,
  RegisterStepShell,
  RoleSelectionStep,
  SignOutConfirmModal,
} from './components'
import { useRegisterPage } from './hooks/use-register-page'
import { REGISTER_TOTAL_PHASES } from './types'

export type RegisterPageProps = {
  onLoadUserProfile: LoadUserProfile
  onSetSession: SetSession
  onUpdateMyProfile: UpdateMyProfile
  onCompleteOnboarding: CompleteOnboarding
  onSendContactClaimOtp: SendContactClaimOtp
  onClaimContact: ClaimContact
  onRecordLegalAcceptance: RecordLegalAcceptance
  onLoadCurrentDocument: LoadCurrentDocument
  onUploadProfilePhoto: UploadProfilePhoto
  onValidatePromoCode: ValidatePromoCode
  onCreateCheckoutSession: CreateCheckoutSession
  onLoadConfig: LoadConfig
  onLoadMyEntitlements: LoadMyEntitlements
  onCreateProCrew: CreateProCrew
  onSendCrewInvite: SendCrewInvite
  onSendOtp: SendOtp
  onVerifyOtp: VerifyOtp
  onEmitFunnelEvent: EmitFunnelEvent
  getAccessToken: () => Promise<string | null>
}

export function RegisterPage({
  onLoadUserProfile,
  onSetSession,
  onUpdateMyProfile,
  onCompleteOnboarding,
  onSendContactClaimOtp,
  onClaimContact,
  onRecordLegalAcceptance,
  onLoadCurrentDocument,
  onUploadProfilePhoto,
  onValidatePromoCode,
  onCreateCheckoutSession,
  onLoadConfig,
  onLoadMyEntitlements,
  onCreateProCrew,
  onSendCrewInvite,
  onSendOtp,
  onVerifyOtp,
  onEmitFunnelEvent,
  getAccessToken,
}: RegisterPageProps) {
  const vm = useRegisterPage({
    onLoadUserProfile,
    onSetSession,
    onUpdateMyProfile,
    onCompleteOnboarding,
    onSendContactClaimOtp,
    onClaimContact,
    onRecordLegalAcceptance,
    onLoadCurrentDocument,
    onUploadProfilePhoto,
    onValidatePromoCode,
    onCreateCheckoutSession,
    onLoadConfig,
    onLoadMyEntitlements,
    onCreateProCrew,
    onSendCrewInvite,
    onSendOtp,
    onVerifyOtp,
    onEmitFunnelEvent,
    getAccessToken,
  })

  if (vm.isBootstrapping) {
    return (
      <div
        data-testid="register-bootstrap"
        className="flex min-h-screen items-center justify-center bg-background"
      >
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <RegisterLayout>
      {vm.isMobile ? null : (
        <header
          data-testid="register-header"
          className="flex items-center justify-between gap-3 border-b px-6 py-4"
        >
          <div className="flex items-center gap-3">
            <img
              src="/blindbookStackedLogo.svg"
              alt="BlindBook"
              className="h-12 w-auto ml-2"
            />
            {vm.canGoBack ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={vm.goBack}
                data-testid="register-step-back"
                aria-label="Go to previous step"
              >
                <ChevronLeft className="size-4" />
                Back
              </Button>
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={vm.openSignOutModal}
            data-testid="register-back-to-menu"
          >
            <LogOut className="size-4" />
            Back to menu
          </Button>
        </header>
      )}

      {vm.isMobile ? null : (
        <RegisterProgressBar currentStep={vm.currentPhase} totalSteps={REGISTER_TOTAL_PHASES} />
      )}

      {vm.isMobile && vm.canGoBackToPersona ? (
        <button
          type="button"
          onClick={vm.goBack}
          data-testid="register-mobile-back"
          aria-label="Go to previous step"
          className="fixed left-2 top-2 z-50 flex size-9 items-center justify-center rounded-full bg-black/40 text-white"
        >
          <ChevronLeft className="size-5" />
        </button>
      ) : null}

      <RegisterStepShell fullBleed={vm.isMobile}>
        {vm.isCheckingProfile || vm.isResolvingCrewGate ? (
          <div
            data-testid="register-checking-profile"
            className="flex items-center justify-center py-12"
          >
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : vm.currentStep === 'auth' ? (
          <AuthStep
            onSendOtp={vm.onSendOtp}
            onVerifyOtp={vm.onVerifyOtp}
            onVerified={vm.goNext}
          />
        ) : vm.currentStep === 'hunterProfile' ? (
          <section data-testid="step-hunterProfile" className="flex flex-col gap-6">
            <RegisterStepHeading
              title="Tell us about yourself"
              subtitle={
                vm.isProfileLocked
                  ? 'Your hunter profile is set. Continue to the next step.'
                  : "We'll use this to personalize your experience."
              }
            />
            <RegisterStepCard>
              <HunterProfileStep
                initialValue={vm.hunterProfileData}
                lockedEmail={vm.sessionEmail}
                locked={vm.isProfileLocked}
                nextOnboardingStep={vm.hunterProfileOnboardingStep}
                onUpdateMyProfile={vm.onUpdateMyProfile}
                onCompleteOnboarding={vm.onCompleteOnboarding}
                onSendContactClaimOtp={vm.onSendContactClaimOtp}
                onClaimContact={vm.onClaimContact}
                onRecordLegalAcceptance={vm.onRecordLegalAcceptance}
                onLoadCurrentDocument={vm.onLoadCurrentDocument}
                onUpload={vm.onUploadProfilePhoto}
                onPersistData={vm.setHunterProfileData}
                onSuccess={vm.handleHunterProfileSuccess}
              />
            </RegisterStepCard>
          </section>
        ) : vm.currentStep === 'roleSelection' ? (
          <section data-testid="step-roleSelection" className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Personas
              </span>
              <h2 className="text-2xl font-bold leading-tight text-foreground">
                Which role best describes you?
              </h2>
              <p className="text-sm text-muted-foreground">You can change this later.</p>
            </div>
            <RoleSelectionStep
              onSelect={vm.handleRoleSelect}
              trialDaysByOrgType={vm.trialDaysByOrgType}
              showCrew={vm.showCrewCard}
              proCrew
            />
          </section>
        ) : vm.currentStep === 'crewExplainer' ? (
          <CrewExplainerStep
            isContinuing={vm.isCrewContinuing}
            message={vm.crewMessage}
            onContinue={vm.handleCrewContinue}
            fullBleed={vm.isMobile}
          />
        ) : vm.currentStep === 'crewName' ? (
          <CrewNameStep
            onCreateProCrew={vm.onCreateProCrew}
            onSendCrewInvite={vm.onSendCrewInvite}
            onProRequired={vm.handleCrewProRequired}
            onCreated={vm.handleCrewCreated}
            fullBleed={vm.isMobile}
          />
        ) : vm.currentStep === 'featurePage' ? (
          <section data-testid="step-featurePage">
            <FeaturePageStep
              orgType={vm.orgType}
              trialDays={vm.trialDaysByOrgType[vm.orgType]}
              fullBleed={vm.isMobile}
              onContinue={(tier) => {
                vm.setBillingCycle(tier)
                vm.goNext()
              }}
            />
          </section>
        ) : vm.currentStep === 'orgForm' ? (
          <section data-testid="step-orgForm" className="flex flex-col gap-6">
            <RegisterStepHeading
              title="Set up your organization"
              subtitle="Tell us about the organization you'll be managing."
            />
            <RegisterStepCard>
              <OrgFormStep
                initialValue={vm.orgFormData}
                orgType={vm.orgType}
                isMobile={vm.isMobile}
                onValidatePromoCode={vm.onValidatePromoCode}
                onPersistData={vm.setOrgFormData}
                onSubmit={vm.goNext}
              />
            </RegisterStepCard>
          </section>
        ) : vm.currentStep === 'checkout' ? (
          <section data-testid="step-checkout" className="flex flex-col gap-6">
            <RegisterStepHeading
              title="Complete payment"
              subtitle="We're setting up your secure checkout session."
            />
            <RegisterStepCard>
              <CheckoutStep
                orgType={vm.orgType}
                billingCycle={vm.billingCycle}
                orgFormData={vm.orgFormData}
                isMobile={vm.isMobile}
                onCreateCheckoutSession={vm.onCreateCheckoutSession}
                onEmitFunnelEvent={vm.onEmitFunnelEvent}
                getAccessToken={vm.getAccessToken}
              />
            </RegisterStepCard>
          </section>
        ) : null}
      </RegisterStepShell>

      <SignOutConfirmModal
        open={vm.isSignOutModalOpen}
        onCancel={vm.closeSignOutModal}
        onConfirm={vm.confirmSignOut}
      />
    </RegisterLayout>
  )
}
